import { __ } from '@wordpress/i18n';
import { useState, useRef, Fragment } from "@wordpress/element";
import { Dialog, Transition } from "@headlessui/react";
import { PaperClipIcon } from "@heroicons/react/solid";
import { ImageSlider, ImageSliderHeader } from './ImageSlider';

type ImageConfig = {
  url: string,
  title: string
};

type AttachmentConfig = {
  url: string,
  filename: string,
};

type VideoConfig = {
  type: "youtube",
  videoId: string,
};

export type GalleryConfig = {
  images: ImageConfig[],
  attachments: AttachmentConfig[],
  videos: VideoConfig[]
};

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

export function Images(props: { list: ImageConfig[], onClick: Function }) {
  return (
    <div className="mt-8">
      <div className="font-bold text-3xl leading-normal">{__("Bilder", "swt-blocks")}</div>
      <div className="mt-4 grid grid-cols-3 gap-2 cursor-pointer">
        {props.list.map((img, k) => (
          <div className="relative overflow-hidden" key={k}>
            <img key={img.url} src={img.url} title={img.title}
                className="object-cover relative w-full h-full max-h-96 hover:scale-110 transition-[transform] duration-500"
                onClick={() => props.onClick(k) }/>
          </div>
        ))}
      </div>
    </div>
  );
}

export function Attachments(props: { list: AttachmentConfig[] }) {
  return (
    <div className="mt-8">
      <div className="font-bold text-3xl leading-normal">{__("Dateien", "swt-blocks")}</div>
      <div className="mt-4 grid grid-cols-3 gap-2">
        <ul role="list" className="border border-gray-200 rounded-md divide-y divide-gray-200">
          {props.list.map((file) => (
            <li key={file.url} className="pl-3 pr-4 py-3 flex items-center justify-between text-sm">
              <div className="w-0 flex-1 flex items-center">
                <PaperClipIcon className="flex-shrink-0 h-5 w-5" aria-hidden="true" />
                <span className="ml-2 flex-1 w-0 truncate">{file.filename}</span>
              </div>
              <div className="ml-4 flex-shrink-0">
                <a href={file.url} target="_blank" className="font-medium text-blue-700 hover:opacity-75">
                  Download
                </a>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export function Videos(props: { list: VideoConfig[] }) {
  return (
    <div className="mt-8">
      <div className="font-bold text-3xl leading-normal">{__("Videos", "swt-blocks")}</div>

      <div className="mt-4 flex flex-wrap gap-4">
        {props.list.map((video) => (
          <swt-media key={video.videoId} type="youtube" class="w-[640px] h-[360px]" video-id={video.videoId}></swt-media>
        ))}
      </div>
    </div>
  );
}

export function Tabs(props: { current: string, config: GalleryConfig, onClick: Function }) {
  const tabs = [];

  if (props.config.images.length > 0) {
    tabs.push({
      id: "images",
      name: __("Bilder", "swt-blocks")
    });
  }

  if (props.config.attachments.length > 0) {
    tabs.push({
      id: "attachments",
      name: __("Dateien", "swt-blocks")
    });
  }

  if (props.config.videos.length > 0) {
    tabs.push({
      id: "videos",
      name: __("Videos", "swt-blocks")
    });
  }

  return (
    <div className="flex space-x-4 items-center">
      {tabs.map((tab) => (
        <button
          key={tab.name}
          onClick={() => props.onClick(tab.id)}
          className={classNames(
            tab.id == props.current ? "swt-bg-primary swt-text-primary" : 'bg-gray-200 text-gray-800 swt-text-primary hover:opacity-75',
            'px-3 py-2 font-medium text-sm rounded-lg'
          )}
        >
          {tab.name}
        </button>
      ))}
    </div>
  )
}

export function Gallery(props: { onSetup: Function, config: GalleryConfig }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<"images" | "imageSlider" | "attachments" | "videos">("images");
  const [sliderImage, setSliderImage] = useState(0);

  let completeButtonRef = useRef(null);

  if (props.onSetup) {
    props.onSetup({
      open: (tabId?: "images" | "attachments" | "videos") => {
        if (tabId) {
          setTab(tabId);
        }
        setOpen(true);
      }
    });
  }

  const sliderImages = [];
  props.config.images.map((img) => {
    sliderImages.push({
      url: img.url,
      title: img.title
    });
  });

  const openSlider = (k: number) => {
    setSliderImage(k);
    setTab("imageSlider");
  };

  const onSlideChange = (k: number) => {
    setSliderImage(k);
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="fixed z-50 inset-0 overflow-y-auto bg-white" onClose={setOpen} initialFocus={completeButtonRef}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          enterTo="opacity-100 translate-y-0 sm:scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 translate-y-0 sm:scale-100"
          leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
        >
          <div className="max-w-screen-xl mx-auto pb-8">
            <div className="py-5 border-b border-gray-200">
              <nav className="flex justify-between">

                  {tab == "imageSlider"
                    ? (
                      <ImageSliderHeader list={sliderImages} current={sliderImage} onBack={() => setTab("images")}></ImageSliderHeader>
                    )
                    : (
                      <Tabs current={tab} config={props.config} onClick={setTab} />
                    )
                  }

                <div className="flex place-content-center">
                  <button className="text-2xl focus:outline-none" onClick={() => setOpen(false)}
                    title={__("Fenster schließen", "swt-blocks")} ref={completeButtonRef}>
                    <i className="fa-solid fa-xmark"></i>
                  </button>
                </div>
              </nav>
            </div>

            <div className="mt-8">
              {tab == "imageSlider" &&
                <ImageSlider list={sliderImages} open={sliderImage} onChange={onSlideChange}></ImageSlider>
              }

              {tab == "images" &&
                <Images list={props.config.images} onClick={openSlider}></Images>
              }

              {tab == "attachments" &&
                <Attachments list={props.config.attachments}></Attachments>
              }

              {tab == "videos" &&
                <Videos list={props.config.videos}></Videos>
              }
            </div>
          </div>
        </Transition.Child>
      </Dialog>
    </Transition.Root>
  )
}
